import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import { getOpsReports } from '../../../api/twoDays/ops.api';
import { Select } from '../../../components/TwoDays/Modal';
import { cn, initials } from '../../../components/TwoDays/model/derive';
import { KST, formatDue } from '../../../components/TwoDays/model/time';
import { jobLabel } from '../../../components/TwoDays/ops/opsModel';
import { StatusChip } from '../../../components/TwoDays/primitives';
import PeerReviewSheet from '../../../components/TwoDays/ops/PeerReviewSheet';
import useMedia, { MOBILE } from '../../../components/TwoDays/useMedia';

/**
 * Ops › Reports (2차 핸드오프 C1~C5) — 프로젝트 참여자별 완주 여부와 Growth Report 상태.
 * 스태프·Staff backup 은 나오지 않는다. 행에 마우스를 올리면 배경이 깔리고, 누르면 Growth Report 를 새 탭으로 연다.
 * Report 칩에 마우스를 올리면(모바일은 탭) 상태 기록 카드가 칩 오른쪽 끝 기준 왼쪽으로 열린다. 바깥 클릭·Esc 로 닫힌다.
 * Peer review 열의 View — 그 사람이 쓴 · 받은 피어리뷰 원문을 시트로 (행 클릭과 따로). 이탈자는 "—"
 */

const COMPLETION = {
  completed: { label: 'Completed', tone: 'success' },
  completed_late: { label: 'Completed · peer review late', tone: 'warning' },
  peer_review_open: { label: 'Peer review open', tone: 'success' },
  // 동료 평가가 열리기 전 — 디자인의 다섯 가지 안에서 Not completed 로 보인다 (리포트는 Waiting)
  in_progress: { label: 'Not completed', tone: 'gray' },
  dropped: { label: 'Dropped', tone: 'gray' },
  not_completed: { label: 'Not completed', tone: 'gray' },
};
const REPORT = {
  not_eligible: { label: 'Not eligible', tone: 'gray', dot: 'bg-grayscale-gray05' },
  waiting: { label: 'Waiting', tone: 'gray', dot: 'bg-grayscale-gray05' },
  draft: { label: 'Draft ready', tone: 'warning', dot: 'bg-functional-warning03' },
  confirmed: { label: 'Confirmed', tone: 'success', dot: 'bg-functional-success03' },
  sent: { label: 'Sent', tone: 'success', dot: 'bg-functional-success03' },
  failed: { label: 'Send failed', tone: 'failed', dot: 'bg-functional-failed03' },
};
const at = (iso) => `${formatDue(iso, KST)} KST`;

const COLS =
  'grid grid-cols-[minmax(200px,1.5fr)_minmax(100px,0.7fr)_minmax(120px,0.8fr)_minmax(200px,1.2fr)_minmax(150px,1fr)_96px] items-center gap-4';

/** 리포트 상태 칩 + 상태 기록 카드 */
const ReportStatus = ({ row }) => {
  const [pos, setPos] = useState(null); // 열려 있으면 카드 위치 (칩 오른쪽 끝 기준)
  const chipRef = useRef(null);
  const cardRef = useRef(null);
  const closeTimer = useRef(null);
  const s = REPORT[row.report] ?? REPORT.waiting;
  // 최신순. 기록이 비어 있으면(동료 평가 열리기 전 등) 현재 상태 한 줄이라도 보여 준다
  const history = row.history?.length
    ? [...row.history].reverse()
    : [{ status: row.report, at: null, note: 'Peer review not open yet' }];
  const open = pos != null;

  const show = () => {
    clearTimeout(closeTimer.current);
    const r = chipRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
  };
  // 칩에서 카드로 마우스를 옮기는 사이 닫히지 않게 조금 기다린다
  const hideSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPos(null), 120);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setPos(null);
    const onKey = (e) => e.key === 'Escape' && close();
    const onDown = (e) =>
      !chipRef.current?.contains(e.target) && !cardRef.current?.contains(e.target) && close();
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div className='flex w-fit flex-col gap-1'>
      <button
        ref={chipRef}
        type='button'
        aria-expanded={open}
        aria-label={`${s.label} · report status history`}
        onMouseEnter={show}
        onMouseLeave={hideSoon}
        onClick={(e) => {
          e.stopPropagation(); // 행 열기와 겹치지 않게 (모바일은 탭으로 카드를 연다)
          if (open) setPos(null);
          else show();
        }}
        className='w-fit rounded-md'
      >
        <StatusChip chip={{ label: s.label, tone: s.tone }} />
      </button>
      {['sent', 'failed'].includes(row.report) && row.reportAt && (
        <span className='text-[13px] text-text-secondary'>{at(row.reportAt)}</span>
      )}
      {open &&
        ReactDOM.createPortal(
          <div
            ref={cardRef}
            role='dialog'
            aria-label='Report status history'
            onMouseEnter={show}
            onMouseLeave={hideSoon}
            style={{ top: pos.top, right: Math.max(16, pos.right) }}
            className='fixed z-[900] w-[340px] max-w-[calc(100vw-32px)] rounded-2xl bg-grayscale-gray00 p-4 text-left shadow-3'
          >
            <p className='mb-3 text-[14px] font-medium text-text-primary'>Report status history</p>
            <ol className='flex flex-col'>
              {history.map((h, i) => (
                <li
                  key={`${h.status}-${h.at}-${i}`}
                  className='relative flex gap-2.5 pb-3 last:pb-0'
                >
                  {/* 점을 잇는 세로선 */}
                  {i < history.length - 1 && (
                    <span className='absolute left-[3.5px] top-3 h-full w-px bg-green-deep03/[0.12]' />
                  )}
                  <span
                    className={cn(
                      'relative mt-[6px] h-2 w-2 shrink-0 rounded-full',
                      (REPORT[h.status] ?? REPORT.waiting).dot,
                    )}
                  />
                  <span className='flex flex-col gap-0.5'>
                    <span className='text-[14px] font-medium text-text-primary'>
                      {(REPORT[h.status] ?? REPORT.waiting).label}
                    </span>
                    {h.at && <span className='text-[13px] text-text-secondary'>{at(h.at)}</span>}
                    {h.note && <span className='text-[13px] text-text-secondary'>{h.note}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </div>,
          document.body,
        )}
    </div>
  );
};

/**
 * 모바일 한 줄 — 이름(+ 직무 · 팀) 왼쪽, 완주 칩 · 리포트 상태 · Peer review 버튼(44px) 오른쪽 세로로.
 * 줄을 누르면 리포트, Peer review 는 시트만
 */
const MobileRow = ({ r, active, onOpen, onPeer }) => {
  const c = COMPLETION[r.completion] ?? COMPLETION.in_progress;
  const s = REPORT[r.report] ?? REPORT.waiting;
  return (
    <div
      role='link'
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 border-t border-green-deep03/[0.06] px-4 py-3 first:border-t-0 focus-visible:outline-none',
        active ? 'bg-green-deep03/[0.03]' : 'active:bg-green-deep03/[0.03]',
      )}
    >
      <span className='flex min-w-0 items-center gap-2.5'>
        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[12px] font-medium text-green-deep04'>
          {initials(r.name)}
        </span>
        <span className='flex min-w-0 flex-col gap-0.5'>
          <span className='flex flex-wrap items-center gap-1.5'>
            <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{r.name}</span>
            {r.leader && <StatusChip chip={{ label: 'Leader', tone: 'newRequest' }} />}
          </span>
          <span className='text-[13px] leading-[140%] text-text-secondary'>
            {jobLabel(r.job)} · {r.teamName}
          </span>
        </span>
      </span>
      <span className='flex flex-col items-end gap-1.5'>
        <StatusChip chip={c} />
        <span className='text-[13px] leading-[140%] text-text-secondary'>{s.label}</span>
        {r.completion !== 'dropped' && (
          <button
            type='button'
            aria-label={`View peer review responses · ${r.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onPeer();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className={cn(
              'inline-flex h-11 items-center whitespace-nowrap rounded-full px-3.5 text-[13px] font-medium text-text-primary',
              active ? 'bg-green-deep03/[0.14]' : 'bg-green-deep03/[0.08] active:bg-green-deep03/[0.14]',
            )}
          >
            Peer review
          </button>
        )}
      </span>
    </div>
  );
};

const OpsReports = () => {
  const { state, dispatch, project } = useOutletContext();
  const mobile = useMedia(MOBILE);
  const projectId = state.currentId;
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [team, setTeam] = useState('all');
  const [peer, setPeer] = useState(null); // 피어리뷰 응답 시트를 연 참여자

  useEffect(() => {
    if (projectId == null) return undefined;
    let alive = true;
    setRows(null);
    setError('');
    setTeam('all');
    const load = () =>
      getOpsReports(projectId)
        .then((list) => alive && setRows(list ?? []))
        .catch(() => alive && setError('Couldn’t load reports. Refresh the page.'));
    load();
    // 리포트 뷰어(새 탭)에서 상태를 바꾸고 돌아오면 새로 받는다
    window.addEventListener('focus', load);
    return () => {
      alive = false;
      window.removeEventListener('focus', load);
    };
  }, [projectId]);

  const teams = project?.teams ?? [];
  const shown = (rows ?? []).filter((r) => team === 'all' || String(r.teamId) === team);
  // 어느 행이든 누르면 Growth Report 를 새 탭으로 연다 (Waiting · Not eligible 은 뷰어가 안내를 띄운다)
  const openReport = (r) => {
    window.open(`/2days/ops/reports/${r.memberId}`, '_blank', 'noopener');
  };

  return (
    <div className='flex max-w-[1400px] flex-col gap-6'>
      <div className='flex flex-col gap-1.5'>
        <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
          Reports
        </h1>
        <p className='text-[14px] text-text-secondary'>
          Everyone who completed the project gets a report. Staff backup accounts aren’t listed.
        </p>
      </div>

      <div className='flex flex-wrap gap-3'>
        <label className='flex w-full max-w-[360px] flex-col gap-1.5'>
          <span className='text-[13px] font-medium text-text-primary'>Project</span>
          <Select
            value={projectId ?? ''}
            onChange={(e) => dispatch({ type: 'select', id: Number(e.target.value) })}
          >
            {state.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </label>
        <label className='flex w-full max-w-[280px] flex-col gap-1.5'>
          <span className='text-[13px] font-medium text-text-primary'>Team</span>
          <Select value={team} onChange={(e) => setTeam(e.target.value)}>
            <option value='all'>All teams</option>
            {teams.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {error ? (
        <p className='text-[14px] text-functional-failed04'>{error}</p>
      ) : rows == null ? (
        <p className='py-10 text-center text-[14px] text-text-secondary'>
          {projectId == null ? 'Pick a project first.' : 'Loading…'}
        </p>
      ) : shown.length === 0 ? (
        <div className='rounded-2xl bg-grayscale-gray00 px-6 py-14 text-center shadow-4'>
          <p className='text-[15px] font-medium text-text-primary'>No participants yet</p>
          <p className='mt-1 text-[14px] text-text-secondary'>
            People show up here once they’re on a team.
          </p>
        </div>
      ) : mobile ? (
        <div className='rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 shadow-4'>
          {shown.map((r) => (
            <MobileRow
              key={r.memberId}
              r={r}
              active={peer?.memberId === r.memberId}
              onOpen={() => openReport(r)}
              onPeer={() => setPeer(r)}
            />
          ))}
        </div>
      ) : (
        <div className='rounded-2xl bg-grayscale-gray00 shadow-4'>
          <div className='overflow-x-auto'>
            <div className='min-w-[1020px]'>
              <div className={cn(COLS, 'px-6 py-3.5 text-[13px] font-medium text-text-secondary')}>
                <span>Participant</span>
                <span>Job</span>
                <span>Team</span>
                <span>Completion</span>
                <span>Report</span>
                <span>Peer review</span>
              </div>
              {shown.map((r) => {
                const c = COMPLETION[r.completion] ?? COMPLETION.in_progress;
                return (
                  <div
                    key={r.memberId}
                    role='link'
                    tabIndex={0}
                    title='Open report in a new tab'
                    onClick={() => openReport(r)}
                    onKeyDown={(e) => e.key === 'Enter' && openReport(r)}
                    className={cn(
                      COLS,
                      'min-h-[68px] border-t border-green-deep03/[0.08] px-6 py-3 text-[15px] text-text-primary transition-colors',
                      peer?.memberId === r.memberId && 'bg-green-deep03/[0.03]',
                      'cursor-pointer hover:bg-green-deep03/[0.03] focus-visible:bg-green-deep03/[0.03] focus-visible:outline-none',
                    )}
                  >
                    <span className='flex min-w-0 items-center gap-3'>
                      <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[13px] font-medium text-green-deep04'>
                        {initials(r.name)}
                      </span>
                      <span className='truncate font-medium'>{r.name}</span>
                      {r.leader && <StatusChip chip={{ label: 'Leader', tone: 'newRequest' }} />}
                    </span>
                    <span>{jobLabel(r.job)}</span>
                    <span className='text-text-secondary'>{r.teamName}</span>
                    <span>
                      <StatusChip chip={c} />
                    </span>
                    <ReportStatus row={r} />
                    <span>
                      {r.completion === 'dropped' ? (
                        <span
                          title='Dropped members aren’t part of peer review'
                          className='text-[14px] text-text-tertiary'
                        >
                          —
                        </span>
                      ) : (
                        <button
                          type='button'
                          aria-label={`View peer review responses · ${r.name}`}
                          onClick={(e) => {
                            // 행 클릭(리포트 열기)과 따로 — 시트만 연다
                            e.stopPropagation();
                            setPeer(r);
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                          className={cn(
                            'inline-flex h-8 items-center whitespace-nowrap rounded-full px-3.5 text-[13px] font-medium text-text-primary transition-colors hover:bg-green-deep03/[0.14]',
                            peer?.memberId === r.memberId
                              ? 'bg-green-deep03/[0.14]'
                              : 'bg-green-deep03/[0.08]',
                          )}
                        >
                          View
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <PeerReviewSheet row={peer} onClose={() => setPeer(null)} />
    </div>
  );
};

export default OpsReports;
